/**
 * Sonnenstand und Tageslänge (astronomisch berechnet, funktioniert offline).
 * Genauigkeit: ca. ±0,5° – mehr als genug für „scheint die Sonne auf die Pflanzen?“.
 */
import type { Room } from './model';

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;

export interface SunPosition {
  /** Himmelsrichtung der Sonne: 0 = Nord, 90 = Ost, 180 = Süd, 270 = West. */
  azimuth: number;
  /** Höhe über dem Horizont in Grad (negativ = unter dem Horizont). */
  elevation: number;
}

function julianDay(date: Date): number {
  return date.getTime() / 86_400_000 + 2440587.5;
}

/** Deklination der Sonne und Rektaszension (Grad) für ein Datum. */
function solarCoords(date: Date): { dec: number; ra: number; n: number } {
  const n = julianDay(date) - 2451545.0;
  const L = (((280.46 + 0.9856474 * n) % 360) + 360) % 360;
  const g = ((((357.528 + 0.9856003 * n) % 360) + 360) % 360) * RAD;
  const lambda = (L + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * RAD;
  const eps = (23.439 - 0.0000004 * n) * RAD;
  const ra = Math.atan2(Math.cos(eps) * Math.sin(lambda), Math.cos(lambda)) * DEG;
  const dec = Math.asin(Math.sin(eps) * Math.sin(lambda)) * DEG;
  return { dec, ra, n };
}

export function sunPosition(date: Date, lat: number, lon: number): SunPosition {
  const { dec, ra, n } = solarCoords(date);
  const gmstHours = (((18.697374558 + 24.06570982441908 * n) % 24) + 24) % 24;
  const lstDeg = gmstHours * 15 + lon;
  const ha = ((((lstDeg - ra) % 360) + 540) % 360) - 180; // Stundenwinkel −180..180
  const phi = lat * RAD;
  const d = dec * RAD;
  const h = ha * RAD;
  const elevation = Math.asin(Math.sin(phi) * Math.sin(d) + Math.cos(phi) * Math.cos(d) * Math.cos(h)) * DEG;
  const azFromSouth = Math.atan2(Math.sin(h), Math.cos(h) * Math.sin(phi) - Math.tan(d) * Math.cos(phi)) * DEG;
  const azimuth = (azFromSouth + 180 + 360) % 360;
  return { azimuth, elevation };
}

/** Tageslänge in Stunden (Sonnenauf- bis -untergang, mit Lichtbrechung). */
export function dayLengthHours(date: Date, lat: number): number {
  const noon = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 12));
  const { dec } = solarCoords(noon);
  const phi = lat * RAD;
  const d = dec * RAD;
  const cosW = (Math.sin(-0.833 * RAD) - Math.sin(phi) * Math.sin(d)) / (Math.cos(phi) * Math.cos(d));
  if (cosW <= -1) return 24;
  if (cosW >= 1) return 0;
  return (2 * Math.acos(cosW) * DEG) / 15;
}

/** Vorzeichenbehafteter Winkelabstand a − b im Bereich (−180, 180]. */
export function angleDiff(a: number, b: number): number {
  const d = (((a - b) % 360) + 540) % 360 - 180;
  return d === -180 ? 180 : d;
}

/**
 * Einstellbare Annahmen zur Raumgeometrie. Die Pflanzen stehen etwas vom Fenster
 * entfernt auf einem Tisch: Steht die Sonne sehr hoch, fällt ihr Licht nur knapp
 * hinter das Fenster und erreicht sie nicht mehr.
 */
export const ROOM_GEOMETRY = {
  /** Tiefer stehende Sonne verschwindet in Wien meist hinter Häusern. */
  minElevation: 8,
  maxElevation: 55,
  /** Wie weit seitlich die Sonne stehen darf, damit sie noch durchs Fenster scheint. */
  maxOffAxis: 80,
};

/**
 * Scheint die Sonne (rein geometrisch) gerade auf die Pflanzen?
 * Blick zum Fenster: Steht die Sonne links vom Fenster (bei einem Südfenster also
 * im Südosten, vormittags), fallen die Strahlen schräg nach rechts in den Raum.
 */
export function sunReachesPlants(pos: SunPosition, room: Pick<Room, 'windowAzimuth' | 'plantSide'>): boolean {
  if (room.windowAzimuth == null) return false;
  if (pos.elevation < ROOM_GEOMETRY.minElevation || pos.elevation > ROOM_GEOMETRY.maxElevation) return false;
  const delta = angleDiff(pos.azimuth, room.windowAzimuth);
  if (Math.abs(delta) > ROOM_GEOMETRY.maxOffAxis) return false;
  switch (room.plantSide) {
    // Pflanzen rechts neben dem Fenster: Licht von links (vormittags bei einem Südfenster)
    case 'right':
      return delta <= 5;
    case 'left':
      return delta >= -5;
    default:
      return Math.abs(delta) <= 50;
  }
}

export function compassLabel(azimuth: number): string {
  const names = ['Norden', 'Nordosten', 'Osten', 'Südosten', 'Süden', 'Südwesten', 'Westen', 'Nordwesten'];
  return names[Math.round((((azimuth % 360) + 360) % 360) / 45) % 8]!;
}
